// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useConfig } from '@hooks/other/useConfig';
import React, { useState } from 'react';
import { ContentCopy } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { fmt } from '../../utils/common';
import { useGetFullTransferObject } from '../../utils/useGetFullTransferObject';
import { DetailsRow } from '../DetailsRow';
import PartyDisplay from '../PartyDisplay';
import { StateCell, Status } from '../StateCell';
import { useCopyToClipboard } from '../useCopyToClipboard';
import { CCExplorerLink } from './CCExplorerLink';
import { DetailsLayout } from './DetailsLayout';
import { RequestType, RowNew } from './types';

interface DetailsProps {
  row?: RowNew;
}

export const Details: React.FC<DetailsProps> = ({ row }) => {
  const copy = useCopyToClipboard();
  const { environment } = useConfig().app;
  const getFullTransferObject = useGetFullTransferObject();
  const [copying, setCopying] = useState(false);

  if (!row) {
    return null;
  }

  const handleCopyTransfer = async () => {
    setCopying(true);
    try {
      const transferObject = await getFullTransferObject(row);
      copy(JSON.stringify(transferObject));
    } catch (error) {
      console.error('Failed to get full transfer object:', error);
    } finally {
      setCopying(false);
    }
  };

  const onCopy = (value: string) => {
    copy(value);
  };
  const details = [
    {
      label: 'state',
      value: (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <StateCell state={row.requestState as Status} reason={row.reason} />
          {row.requestType === RequestType.Transfer && (
            <Tooltip title="Click to copy transfer details">
              <IconButton
                sx={{ ml: 'auto' }}
                onClick={() => {
                  handleCopyTransfer().catch(() => {});
                }}
                disabled={copying}
                size="small"
              >
                {copying ? <CircularProgress size={16} /> : <ContentCopy />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      label: 'Instrument ID',
      value: row.instrumentId,
    },
    {
      label: 'amount',
      value: fmt(row.amount, 2),
    },
    {
      label: 'registrar',
      value: <PartyDisplay party={row.registrar} />,
    },
    {
      label: 'sender',
      value: <PartyDisplay party={row.sender} />,
    },
    {
      label: 'receiver',
      value: <PartyDisplay party={row.receiver} />,
    },
    {
      label: 'operator',
      value: <PartyDisplay party={row.operator} />,
    },
    {
      label: 'reference',
      value: row.reference,
    },

    {
      label: 'Created at',
      value: (
        <Typography variant="caption" fontFamily="monospace">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      label: 'Template ID',
      value: (
        <Box onClick={() => onCopy(row.templateId)} sx={{ cursor: 'pointer' }}>
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            variant="caption"
          >
            {row.templateId}
          </Typography>
        </Box>
      ),
    },
  ];
  if (row.executeBefore !== undefined) {
    details.splice(1, 0, {
      label: 'execute before (local time)',
      value: `${new Date(row.executeBefore).toLocaleString()}`,
    });
  }
  if (row.inputHoldingCids?.length) {
    details.push({
      label: 'Input holding Cids',
      value: (
        <Box sx={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap' }}>
          {row.inputHoldingCids?.map((cid) => (
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              key={cid}
              variant="caption"
            >
              {cid}
            </Typography>
          ))}
        </Box>
      ),
    });
  }

  if (row.reason) {
    details.splice(1, 0, {
      label: 'reason',
      value: row.reason,
    });
  }

  if (environment) {
    details.push({
      label: 'CC Explorer',
      value: <CCExplorerLink contractId={row.contractId} />,
    });
  }

  return (
    <DetailsLayout>
      {details.map((detail) => (
        <DetailsRow key={detail.label} value={detail.value} label={detail.label} />
      ))}
      {row.requestType === RequestType.Transfer && (
        <Button
          onClick={() => {
            handleCopyTransfer().catch(() => {});
          }}
          disabled={copying}
          size="small"
          variant="outlined"
          sx={{ mt: 2 }}
          color="inherit"
          startIcon={copying ? <CircularProgress size={14} /> : undefined}
        >
          {copying ? 'Fetching...' : 'Copy details'}
        </Button>
      )}
    </DetailsLayout>
  );
};
